import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import appConfig from '../../../config/appConfig';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useGetConnectorsHandler } from '../dataHandlers/getConnectorsHandler';
import { useSchemaTargetsHandler } from '../dataHandlers/getSchemaHandler';
import { useCreateConnectorHandler, useCreateVersionHandler } from '../dataHandlers/mutateConnectorHandlers';
import CreateConnectorDialog from '../components/CreateConnectorDialog';
import { buildInitialRawConfig } from '../utils/dslPayloadBuilder';
import { getApiErrorMessage, getRequiredFields } from '../dataHandlers/connectorsApi';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';

const ConnectorsListPage = () => {
  useDocumentTitle('Connectors');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { connectors, isLoading, refetch } = useGetConnectorsHandler();
  const { targets, marminApiVersions } = useSchemaTargetsHandler();

  const { mutate: createConnector, isPending: isCreatingConnector } = useCreateConnectorHandler();
  const { mutateAsync: createVersion, isPending: isCreatingVersion } = useCreateVersionHandler({
    onSuccess: (version, variables) => {
      navigate(`/connectors/${variables.connectorKey}/versions/${version.version}/builder`);
    },
  });

  const handleCreate = async ({ key, name, documentType, marminApiVersion }) => {
    createConnector(
      { key, name, documentType },
      {
        onSuccess: async () => {
          try {
            const manifest = await getRequiredFields(documentType, marminApiVersion);
            const rawConfig = buildInitialRawConfig({
              target: documentType,
              manifest,
              mappingId: key,
            });

            await createVersion({
              connectorKey: key,
              payload: { marminApiVersion, rawConfig },
            });
            setDialogOpen(false);
            refetch();
          } catch (error) {
            dispatch(
              showSnackbar({
                message: getApiErrorMessage(error),
                severity: 'error',
              }),
            );
          }
        },
      },
    );
  };

  return (
    <Box
      sx={{
        height: '86vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        p: 1,
        pb: 4,
        color: '#fff',
        ml: { xs: 2, md: 10 },
        mr: { xs: 2, md: 10 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Connectors
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Map source ERP invoices to {appConfig.brandName} document fields.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          New connector
        </Button>
      </Stack>

      <Card sx={{ backgroundColor: 'background.paper' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Document type</TableCell>
                  <TableCell>Active version</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {connectors.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No connectors yet. Create one to start mapping fields.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {connectors.map((row) => (
                  <TableRow
                    key={row.id || row.key}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/connectors/${encodeURIComponent(row.key)}`)}
                  >
                    <TableCell>{row.key}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {row.document_type}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.active_version ?? '—'}</TableCell>
                    <TableCell>
                      <Chip label={row.status} size="small" color={row.status === 'ACTIVE' ? 'success' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <CreateConnectorDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleCreate}
        isPending={isCreatingConnector || isCreatingVersion}
        targets={targets}
        marminApiVersions={marminApiVersions}
      />
    </Box>
  );
};

export default ConnectorsListPage;
